import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle,
  Database,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  Upload,
  Pencil,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { EnhancedMappingTab } from "@/components/Admin/EnhancedMappingTab";
import { FTGManualTab } from "@/components/Admin/FTGManualTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultSelectedFieldKeys = [
  "name",
  "price",
  "rrp_price",
  "collection_name",
  "category",
  "categories",
  "product_type",
  "images",
];

const defaultScrapeItemKeys = [
  "sku",
  "description",
  "category",
  "product_type",
  "colour",
  "size_cm",
  "assembled_weight_kg",
  "images",
  "name",
  "price",
  "rrp_price",
];

const statusBadgeClasses = {
  new: "bg-green-100 text-green-800",
  updated: "bg-blue-100 text-blue-800",
  unchanged: "bg-gray-100 text-gray-700",
};

export const AdminImportManagerPage = () => {
  const [activeTab, setActiveTab] = useState("scraper");

  const [sourceUrl, setSourceUrl] = useState("https://www.boca-living.com/collections.html");
  const [sourceCollections, setSourceCollections] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [scrapeItemOptions, setScrapeItemOptions] = useState([]);
  const [selectedScrapeItems, setSelectedScrapeItems] = useState(defaultScrapeItemKeys);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionSearch, setCollectionSearch] = useState("");
  const [selectedCollectionUrls, setSelectedCollectionUrls] = useState([]);

  const [previewJob, setPreviewJob] = useState(null);
  const [startingPreview, setStartingPreview] = useState(false);
  const [selectedFields, setSelectedFields] = useState(defaultSelectedFieldKeys);
  const [replaceExistingImages, setReplaceExistingImages] = useState(true);
  const [rowFilter, setRowFilter] = useState("all");
  const [diffSearch, setDiffSearch] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [publishingProducts, setPublishingProducts] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  const [descriptions, setDescriptions] = useState([]);
  const [descriptionSearch, setDescriptionSearch] = useState("");
  const [descriptionEdits, setDescriptionEdits] = useState({});
  const [descriptionsLoading, setDescriptionsLoading] = useState(false);
  const [publishingDescriptions, setPublishingDescriptions] = useState(false);

  const fetchSourceCollections = async (forceRefresh = false) => {
    setCollectionsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/import-manager/source-collections`, {
        params: {
          source_url: sourceUrl,
          force_refresh: forceRefresh,
        },
      });
      setSourceCollections(res.data.collections || []);
      setFieldOptions(res.data.update_fields || []);
      setScrapeItemOptions(res.data.scrape_items || []);

      const apiDefaults = res.data.default_scrape_items || defaultScrapeItemKeys;
      if ((res.data.scrape_items || []).length > 0) {
        const validDefaults = apiDefaults.filter((itemKey) =>
          (res.data.scrape_items || []).some((item) => item.key === itemKey)
        );
        setSelectedScrapeItems(validDefaults.length > 0 ? validDefaults : (res.data.scrape_items || []).map((item) => item.key));
      }

      if ((res.data.collections || []).length > 0 && selectedCollectionUrls.length === 0) {
        setSelectedCollectionUrls([res.data.collections[0].url]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch source collections");
    }
    setCollectionsLoading(false);
  };

  const fetchDescriptions = async () => {
    setDescriptionsLoading(true);
    try {
      const res = await axios.get(`${API}/collection-descriptions`);
      setDescriptions(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load collection descriptions");
    }
    setDescriptionsLoading(false);
  };

  useEffect(() => {
    fetchSourceCollections(false);
    fetchDescriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!previewJob?.job_id) return;
    if (!["pending", "running"].includes(previewJob.status)) return;

    const intervalId = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/import-manager/preview/${previewJob.job_id}`);
        const jobData = res.data;
        setPreviewJob(jobData);

        if ((jobData.scrape_items || []).length > 0) {
          setScrapeItemOptions(jobData.scrape_items || []);
        }

        if (jobData.status === "completed") {
          const changedRows = (jobData.rows || []).filter((row) => row.status !== "unchanged");
          setSelectedRowIds(changedRows.map((row) => row.row_id));
          if ((jobData.update_fields || []).length > 0) {
            const validDefault = defaultSelectedFieldKeys.filter((key) =>
              (jobData.update_fields || []).some((fieldOption) => fieldOption.key === key)
            );
            setSelectedFields(validDefault.length > 0 ? validDefault : (jobData.update_fields || []).map((field) => field.key));
            setFieldOptions(jobData.update_fields || []);
          }
          clearInterval(intervalId);
        }

        if (jobData.status === "failed") {
          toast.error(jobData.error || "Preview scrape failed");
          clearInterval(intervalId);
        }
      } catch (error) {
        clearInterval(intervalId);
        toast.error(error.response?.data?.detail || "Failed to fetch preview job status");
      }
    }, 2200);

    return () => clearInterval(intervalId);
  }, [previewJob]);

  const filteredCollections = useMemo(() => {
    const term = collectionSearch.trim().toLowerCase();
    if (!term) return sourceCollections;
    return sourceCollections.filter((collection) =>
      `${collection.name} ${collection.description || ""}`.toLowerCase().includes(term)
    );
  }, [sourceCollections, collectionSearch]);

  const previewRows = previewJob?.rows || [];
  const filteredRows = useMemo(() => {
    const searchTerm = diffSearch.trim().toLowerCase();
    return previewRows.filter((row) => {
      const matchesStatus = rowFilter === "all" ? true : row.status === rowFilter;
      const matchesSearch =
        searchTerm.length === 0
          ? true
          : `${row.name || ""} ${row.collection_name || ""} ${row.product_url || ""}`
              .toLowerCase()
              .includes(searchTerm);
      return matchesStatus && matchesSearch;
    });
  }, [previewRows, rowFilter, diffSearch]);

  const filteredDescriptions = useMemo(() => {
    const term = descriptionSearch.trim().toLowerCase();
    return descriptions.filter((item) =>
      `${item.collection_name} ${item.description || ""}`.toLowerCase().includes(term)
    );
  }, [descriptions, descriptionSearch]);

  const toggleCollectionSelection = (collectionUrl) => {
    setSelectedCollectionUrls((prev) =>
      prev.includes(collectionUrl) ? prev.filter((url) => url !== collectionUrl) : [...prev, collectionUrl]
    );
  };

  const toggleSelectAllFilteredCollections = () => {
    const visibleUrls = filteredCollections.map((collection) => collection.url);
    const allVisibleSelected = visibleUrls.every((url) => selectedCollectionUrls.includes(url));
    if (allVisibleSelected) {
      setSelectedCollectionUrls((prev) => prev.filter((url) => !visibleUrls.includes(url)));
      return;
    }
    setSelectedCollectionUrls((prev) => Array.from(new Set([...prev, ...visibleUrls])));
  };

  const startPreviewScrape = async () => {
    if (selectedCollectionUrls.length === 0) {
      toast.error("Please select at least one collection");
      return;
    }
    if (selectedScrapeItems.length === 0) {
      toast.error("Please select at least one data item to scrape");
      return;
    }

    setStartingPreview(true);
    setPublishResult(null);
    try {
      const res = await axios.post(`${API}/admin/import-manager/preview`, {
        source_url: sourceUrl,
        collection_urls: selectedCollectionUrls,
        scrape_items: selectedScrapeItems,
      });
      setPreviewJob({
        job_id: res.data.job_id,
        status: res.data.status,
        total_collections: res.data.total_collections,
        selected_scrape_items: res.data.selected_scrape_items || selectedScrapeItems,
        scraped_collections: 0,
        total_products: 0,
      });
      toast.success("Scrape preview started");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start scrape preview");
    }
    setStartingPreview(false);
  };

  const toggleFieldSelection = (fieldKey) => {
    setSelectedFields((prev) =>
      prev.includes(fieldKey) ? prev.filter((key) => key !== fieldKey) : [...prev, fieldKey]
    );
  };

  const toggleScrapeItemSelection = (itemKey) => {
    setSelectedScrapeItems((prev) =>
      prev.includes(itemKey) ? prev.filter((key) => key !== itemKey) : [...prev, itemKey]
    );
  };

  const toggleRowSelection = (rowId) => {
    setSelectedRowIds((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  };

  const toggleSelectAllVisibleRows = () => {
    const visibleChangedRows = filteredRows.filter((row) => row.status !== "unchanged").map((row) => row.row_id);
    const allSelected = visibleChangedRows.length > 0 && visibleChangedRows.every((rowId) => selectedRowIds.includes(rowId));
    if (allSelected) {
      setSelectedRowIds((prev) => prev.filter((rowId) => !visibleChangedRows.includes(rowId)));
      return;
    }
    setSelectedRowIds((prev) => Array.from(new Set([...prev, ...visibleChangedRows])));
  };

  const publishProductUpdates = async () => {
    if (!previewJob?.job_id) {
      toast.error("No preview job found");
      return;
    }
    if (selectedFields.length === 0) {
      toast.error("Please select at least one field to update");
      return;
    }

    setPublishingProducts(true);
    try {
      const res = await axios.post(`${API}/admin/import-manager/commit`, {
        job_id: previewJob.job_id,
        selected_fields: selectedFields,
        selected_row_ids: selectedRowIds,
        replace_existing_images: replaceExistingImages,
      });
      setPublishResult(res.data);
      if (res.data.success) {
        toast.success("Product updates published successfully");
      } else {
        toast.error("Published with some errors");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish product updates");
    }
    setPublishingProducts(false);
  };

  const toggleRowExpand = (rowId) => {
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  const getDescriptionDraft = (item) => {
    if (Object.prototype.hasOwnProperty.call(descriptionEdits, item.collection_name)) {
      return descriptionEdits[item.collection_name];
    }
    return item.description || "";
  };

  const updateDescriptionDraft = (collectionName, nextValue) => {
    setDescriptionEdits((prev) => ({ ...prev, [collectionName]: nextValue }));
  };

  const discardDescriptionEdits = () => {
    setDescriptionEdits({});
  };

  const publishDescriptionUpdates = async () => {
    const editedItems = descriptions
      .filter((item) => Object.prototype.hasOwnProperty.call(descriptionEdits, item.collection_name))
      .map((item) => ({
        collection_name: item.collection_name,
        description: descriptionEdits[item.collection_name],
        source_url: item.source_url || null,
      }))
      .filter((item) => item.description && item.description.trim().length > 0);

    if (editedItems.length === 0) {
      toast.info("No edited descriptions to publish");
      return;
    }

    setPublishingDescriptions(true);
    try {
      await axios.post(`${API}/admin/collection-descriptions/bulk-upsert`, {
        items: editedItems,
        overwrite_existing: true,
      });
      toast.success(`Published ${editedItems.length} description updates`);
      setDescriptionEdits({});
      fetchDescriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish description updates");
    }
    setPublishingDescriptions(false);
  };

  return (
    <div data-testid="admin-import-manager-page">
      <h1 className="font-serif text-3xl mb-2">Import Manager</h1>
      <p className="text-warm-muted mb-6" data-testid="import-manager-page-subtitle">
        Scrape selected collections from Boca Living, preview full row-level diffs, and publish only the fields you choose.
      </p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="import-manager-tab-list">
        <Button
          onClick={() => setActiveTab("scraper")}
          variant={activeTab === "scraper" ? "default" : "outline"}
          data-testid="import-manager-scraper-tab-button"
        >
          <Database className="w-4 h-4 mr-2" />
          Collection Scraper
        </Button>
        <Button
          onClick={() => setActiveTab("enhanced-mapping")}
          variant={activeTab === "enhanced-mapping" ? "default" : "outline"}
          data-testid="import-manager-enhanced-mapping-tab-button"
        >
          <Database className="w-4 h-4 mr-2" />
          Enhance Mapping
        </Button>
        <Button
          onClick={() => setActiveTab("ftg-manual")}
          variant={activeTab === "ftg-manual" ? "default" : "outline"}
          data-testid="import-manager-ftg-manual-tab-button"
        >
          <Upload className="w-4 h-4 mr-2" />
          FTG Manual
        </Button>
        <Button
          onClick={() => setActiveTab("descriptions")}
          variant={activeTab === "descriptions" ? "default" : "outline"}
          data-testid="import-manager-descriptions-tab-button"
        >
          <Pencil className="w-4 h-4 mr-2" />
          Collection Description Manager
        </Button>
      </div>

      {activeTab === "scraper" && (
        <div className="space-y-6" data-testid="import-manager-scraper-tab-content">
          <div className="bg-white rounded-lg shadow-warm p-6" data-testid="source-collection-selection-card">
            <div className="flex flex-col xl:flex-row gap-4 xl:items-end justify-between mb-4">
              <div className="flex-1">
                <label className="text-sm font-medium block mb-2">Source Collections URL</label>
                <Input
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  data-testid="import-manager-source-url-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => fetchSourceCollections(true)}
                  disabled={collectionsLoading}
                  data-testid="import-manager-refresh-collections-button"
                >
                  {collectionsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Refresh
                </Button>
                <Button
                  onClick={startPreviewScrape}
                  disabled={startingPreview || selectedCollectionUrls.length === 0 || selectedScrapeItems.length === 0}
                  data-testid="import-manager-start-preview-button"
                >
                  {startingPreview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                  Start Preview
                </Button>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between mb-3">
              <div className="relative md:w-[380px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  className="pl-9"
                  placeholder="Search collections"
                  value={collectionSearch}
                  onChange={(e) => setCollectionSearch(e.target.value)}
                  data-testid="import-manager-collection-search-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={toggleSelectAllFilteredCollections}
                  data-testid="import-manager-select-visible-collections-button"
                >
                  Toggle Visible
                </Button>
                <Badge variant="secondary" data-testid="import-manager-selected-collections-count">
                  {selectedCollectionUrls.length} selected
                </Badge>
              </div>
            </div>

            <div className="max-h-[360px] overflow-y-auto border rounded-lg" data-testid="import-manager-source-collections-list">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr>
                    <th className="p-2 text-left w-12">Select</th>
                    <th className="p-2 text-left">Collection</th>
                    <th className="p-2 text-left">Summary</th>
                    <th className="p-2 text-left">Source URL</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCollections.map((collection) => (
                    <tr key={collection.url} className="border-t">
                      <td className="p-2 align-top">
                        <input
                          type="checkbox"
                          checked={selectedCollectionUrls.includes(collection.url)}
                          onChange={() => toggleCollectionSelection(collection.url)}
                          data-testid={`import-manager-collection-checkbox-${collection.name.replace(/\s+/g, "-").toLowerCase()}`}
                        />
                      </td>
                      <td className="p-2 align-top font-medium" data-testid={`import-manager-collection-name-${collection.name.replace(/\s+/g, "-").toLowerCase()}`}>
                        {collection.name}
                      </td>
                      <td className="p-2 align-top text-gray-600">{collection.description || "—"}</td>
                      <td className="p-2 align-top text-xs break-all text-blue-700">{collection.url}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selectedCollectionUrls.length > 0 && (
            <div className="bg-white rounded-lg shadow-warm p-6" data-testid="import-manager-scrape-items-card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <h2 className="font-medium text-lg" data-testid="import-manager-scrape-items-title">Data Items to Scrape</h2>
                  <p className="text-sm text-gray-600" data-testid="import-manager-scrape-items-subtitle">
                    Choose what to scrape for selected collections before running preview.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const optionKeys = (scrapeItemOptions || []).map((item) => item.key);
                      const defaults = defaultScrapeItemKeys.filter((key) => optionKeys.includes(key));
                      setSelectedScrapeItems(defaults.length > 0 ? defaults : optionKeys);
                    }}
                    data-testid="import-manager-scrape-items-default-button"
                  >
                    Restore Defaults
                  </Button>
                  <Badge variant="secondary" data-testid="import-manager-selected-scrape-items-count">
                    {selectedScrapeItems.length} selected
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2" data-testid="import-manager-scrape-items-grid">
                {(scrapeItemOptions || []).map((item) => {
                  const isSelected = selectedScrapeItems.includes(item.key);
                  const isDefault = !!item.default_selected;
                  return (
                    <label
                      key={item.key}
                      className={`flex items-start gap-2 border rounded px-3 py-2 text-sm transition-colors ${
                        isDefault ? "border-blue-300 bg-blue-50" : "border-gray-200"
                      } ${isSelected ? "ring-1 ring-blue-400" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleScrapeItemSelection(item.key)}
                        data-testid={`import-manager-scrape-item-checkbox-${item.key}`}
                      />
                      <span className="flex-1">
                        <span className="font-medium">{item.label}</span>
                      </span>
                      {isDefault && (
                        <Badge className="bg-blue-600 text-white" data-testid={`import-manager-scrape-item-default-badge-${item.key}`}>
                          Default
                        </Badge>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {previewJob && (
            <div className="bg-white rounded-lg shadow-warm p-6" data-testid="import-manager-preview-status-card">
              <div className="flex flex-wrap items-center gap-3 justify-between mb-3">
                <div>
                  <p className="text-sm text-gray-600">Preview Job</p>
                  <p className="font-mono text-xs text-gray-500" data-testid="import-manager-preview-job-id">{previewJob.job_id}</p>
                </div>
                <Badge className={statusBadgeClasses[previewJob.status] || "bg-yellow-100 text-yellow-800"} data-testid="import-manager-preview-status-badge">
                  {previewJob.status}
                </Badge>
              </div>
              <div className="w-full bg-gray-200 rounded h-2 mb-3" data-testid="import-manager-progress-track">
                <div
                  className="bg-[#003366] h-2 rounded"
                  style={{ width: `${previewJob.progress_percentage || 0}%` }}
                  data-testid="import-manager-progress-bar"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 text-sm" data-testid="import-manager-preview-metrics">
                <div className="p-3 rounded border bg-gray-50">
                  <p className="text-gray-500">Collections</p>
                  <p className="font-semibold">{previewJob.scraped_collections || 0}/{previewJob.total_collections || 0}</p>
                </div>
                <div className="p-3 rounded border bg-gray-50">
                  <p className="text-gray-500">Rows</p>
                  <p className="font-semibold">{previewJob.total_products || 0}</p>
                </div>
                <div className="p-3 rounded border bg-green-50">
                  <p className="text-gray-500">New</p>
                  <p className="font-semibold">{previewJob.new_count || 0}</p>
                </div>
                <div className="p-3 rounded border bg-blue-50">
                  <p className="text-gray-500">Updated</p>
                  <p className="font-semibold">{previewJob.updated_count || 0}</p>
                </div>
                <div className="p-3 rounded border bg-gray-50">
                  <p className="text-gray-500">Unchanged</p>
                  <p className="font-semibold">{previewJob.unchanged_count || 0}</p>
                </div>
                <div className="p-3 rounded border bg-purple-50" data-testid="import-manager-preview-description-count">
                  <p className="text-gray-500">Description Preview</p>
                  <p className="font-semibold">{previewJob.description_preview_count || 0}</p>
                </div>
              </div>

              <div className="mt-3" data-testid="import-manager-preview-scrape-items-summary">
                <p className="text-sm text-gray-600 mb-2">Scrape items used:</p>
                <div className="flex flex-wrap gap-2">
                  {(previewJob.selected_scrape_items || selectedScrapeItems || []).map((itemKey) => (
                    <Badge key={itemKey} variant="outline" data-testid={`import-manager-preview-scrape-item-${itemKey}`}>
                      {itemKey}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {previewJob?.status === "completed" && (
            <div className="bg-white rounded-lg shadow-warm p-6" data-testid="import-manager-diff-card">
              <h2 className="font-medium text-lg mb-3">Row-level Diff Preview</h2>

              <div className="mb-4" data-testid="import-manager-field-selection-block">
                <p className="text-sm text-gray-600 mb-2">Select fields to update in existing products:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
                  {(fieldOptions || []).map((field) => (
                    <label key={field.key} className="flex items-center gap-2 border rounded px-2 py-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(field.key)}
                        onChange={() => toggleFieldSelection(field.key)}
                        data-testid={`import-manager-update-field-checkbox-${field.key}`}
                      />
                      <span>{field.label}</span>
                    </label>
                  ))}
                </div>

                <div className="mt-3 p-3 border rounded bg-gray-50" data-testid="import-manager-image-replace-option-block">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={replaceExistingImages}
                      onChange={(e) => setReplaceExistingImages(e.target.checked)}
                      disabled={!selectedFields.includes("images")}
                      data-testid="import-manager-replace-existing-images-checkbox"
                    />
                    <span>
                      Replace existing DB images when publishing <strong>Images</strong> field
                      {!selectedFields.includes("images") && " (enable Images field to activate)"}
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex flex-col xl:flex-row gap-3 xl:items-center justify-between mb-3">
                <div className="flex gap-2 flex-wrap" data-testid="import-manager-row-filter-buttons">
                  {[
                    { key: "all", label: "All" },
                    { key: "new", label: "New" },
                    { key: "updated", label: "Updated" },
                    { key: "unchanged", label: "Unchanged" },
                  ].map((item) => (
                    <Button
                      key={item.key}
                      variant={rowFilter === item.key ? "default" : "outline"}
                      size="sm"
                      onClick={() => setRowFilter(item.key)}
                      data-testid={`import-manager-row-filter-${item.key}`}
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    placeholder="Search diff rows"
                    value={diffSearch}
                    onChange={(e) => setDiffSearch(e.target.value)}
                    className="xl:w-[300px]"
                    data-testid="import-manager-diff-search-input"
                  />
                  <Button
                    variant="outline"
                    onClick={toggleSelectAllVisibleRows}
                    data-testid="import-manager-toggle-visible-rows-button"
                  >
                    Toggle Visible
                  </Button>
                </div>
              </div>

              <div className="mb-3 flex items-center gap-3" data-testid="import-manager-selected-rows-summary">
                <Badge variant="secondary">{selectedRowIds.length} rows selected for publish</Badge>
              </div>

              <div className="max-h-[520px] overflow-y-auto border rounded-lg" data-testid="import-manager-diff-table">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="p-2 text-left w-12">Pick</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Collection</th>
                      <th className="p-2 text-left">Changed Fields</th>
                      <th className="p-2 text-left">Source URL</th>
                      <th className="p-2 text-left w-24">Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const rowHasChanges = row.status !== "unchanged";
                      const rowExpanded = !!expandedRows[row.row_id];
                      return (
                        <tr key={row.row_id} className="border-t align-top">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedRowIds.includes(row.row_id)}
                              disabled={!rowHasChanges}
                              onChange={() => toggleRowSelection(row.row_id)}
                              data-testid={`import-manager-diff-row-checkbox-${row.row_id}`}
                            />
                          </td>
                          <td className="p-2">
                            <Badge className={statusBadgeClasses[row.status] || "bg-yellow-100 text-yellow-800"}>{row.status}</Badge>
                          </td>
                          <td className="p-2 font-medium">{row.name}</td>
                          <td className="p-2">{row.collection_name}</td>
                          <td className="p-2">{row.changed_fields?.length || 0}</td>
                          <td className="p-2 text-xs break-all text-blue-700">{row.product_url}</td>
                          <td className="p-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleRowExpand(row.row_id)}
                              data-testid={`import-manager-toggle-row-diff-${row.row_id}`}
                            >
                              {rowExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredRows.map((row) => {
                      const rowExpanded = !!expandedRows[row.row_id];
                      if (!rowExpanded) return null;
                      return (
                        <tr key={`${row.row_id}-expanded`} className="bg-gray-50">
                            <td className="p-2">
                            <span className="text-xs text-gray-500">↳</span>
                          </td>
                          <td className="p-3" colSpan={6}>
                            {Object.keys(row.changed_values || {}).length === 0 ? (
                              <p className="text-sm text-gray-500">No field-level changes for this row.</p>
                            ) : (
                              <div className="space-y-2" data-testid={`import-manager-row-diff-details-${row.row_id}`}>
                                {Object.entries(row.changed_values || {}).map(([fieldKey, value]) => (
                                  <div key={fieldKey} className="border rounded p-2 bg-white">
                                    <p className="font-medium text-sm">{fieldKey}</p>
                                    <p className="text-xs text-gray-500 mt-1">From:</p>
                                    <pre className="text-xs whitespace-pre-wrap break-words bg-gray-100 p-2 rounded">{JSON.stringify(value.from, null, 2)}</pre>
                                    <p className="text-xs text-gray-500 mt-2">To:</p>
                                    <pre className="text-xs whitespace-pre-wrap break-words bg-blue-50 p-2 rounded">{JSON.stringify(value.to, null, 2)}</pre>
                                  </div>
                                ))}
                              </div>
                            )}
                            </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="import-manager-publish-action-row">
                <Button
                  onClick={publishProductUpdates}
                  disabled={publishingProducts || selectedRowIds.length === 0 || selectedFields.length === 0}
                  data-testid="import-manager-publish-products-button"
                >
                  {publishingProducts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Publish Product Updates
                </Button>
                <p className="text-sm text-gray-600" data-testid="import-manager-publish-summary-text">
                  Publishing {selectedRowIds.length} rows with {selectedFields.length} selected fields.
                  {selectedFields.includes("images") && ` Images mode: ${replaceExistingImages ? "replace existing" : "append/merge"}.`}
                </p>
              </div>

              {publishResult && (
                <div className={`mt-4 rounded border p-4 ${publishResult.success ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`} data-testid="import-manager-publish-result-card">
                  <div className="flex items-center gap-2 mb-2">
                    {publishResult.success ? <CheckCircle className="w-5 h-5 text-green-700" /> : <RefreshCw className="w-5 h-5 text-yellow-700" />}
                    <p className="font-medium">Publish Result</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-gray-600">Inserted:</span> <strong data-testid="import-manager-publish-inserted-count">{publishResult.inserted}</strong></div>
                    <div><span className="text-gray-600">Updated:</span> <strong data-testid="import-manager-publish-updated-count">{publishResult.updated}</strong></div>
                    <div><span className="text-gray-600">Skipped:</span> <strong data-testid="import-manager-publish-skipped-count">{publishResult.skipped}</strong></div>
                    <div><span className="text-gray-600">Errors:</span> <strong data-testid="import-manager-publish-error-count">{publishResult.errors?.length || 0}</strong></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "descriptions" && (
        <div className="bg-white rounded-lg shadow-warm p-6" data-testid="import-manager-descriptions-tab-content">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between mb-4">
            <div className="relative lg:w-[420px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={descriptionSearch}
                onChange={(e) => setDescriptionSearch(e.target.value)}
                className="pl-9"
                placeholder="Search by collection or text"
                data-testid="description-manager-search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={discardDescriptionEdits} data-testid="description-manager-discard-button">
                Discard Edits
              </Button>
              <Button
                onClick={publishDescriptionUpdates}
                disabled={publishingDescriptions}
                data-testid="description-manager-publish-button"
              >
                {publishingDescriptions ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Publish Description Changes
              </Button>
              <Button variant="outline" onClick={fetchDescriptions} data-testid="description-manager-refresh-button">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-3" data-testid="description-manager-count-label">
            {filteredDescriptions.length} collections shown • {Object.keys(descriptionEdits).length} edited draft(s)
          </p>

          {descriptionsLoading ? (
            <div className="flex items-center gap-2 text-gray-600" data-testid="description-manager-loading-state">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading collection descriptions...
            </div>
          ) : (
            <div className="max-h-[640px] overflow-y-auto border rounded-lg" data-testid="description-manager-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr>
                    <th className="p-2 text-left w-[220px]">Collection</th>
                    <th className="p-2 text-left">Description (inline editable)</th>
                    <th className="p-2 text-left w-[280px]">Source URL</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDescriptions.map((item) => (
                    <tr key={item.collection_name} className="border-t align-top">
                      <td className="p-2 font-medium" data-testid={`description-manager-name-${item.collection_name.replace(/\s+/g, "-").toLowerCase()}`}>
                        {item.collection_name}
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={getDescriptionDraft(item)}
                          rows={4}
                          onChange={(e) => updateDescriptionDraft(item.collection_name, e.target.value)}
                          data-testid={`description-manager-input-${item.collection_name.replace(/\s+/g, "-").toLowerCase()}`}
                        />
                      </td>
                      <td className="p-2 text-xs break-all text-blue-700">{item.source_url || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "enhanced-mapping" && <EnhancedMappingTab />}
      {activeTab === "ftg-manual" && <FTGManualTab />}
    </div>
  );
};
